import { useState } from "react";
import {
  Card, Table, Button, Modal, Form, Input, Select, Tag, message, Popconfirm,
} from "antd";
import { Plus, Power } from "lucide-react";
import { useAdminList, useAdminCreate, useAdminToggleActive } from "../../../api-hooks/admins";
import { TPlatformAdmin } from "../../../types";
import { useAuthStore } from "../../../store/useAuthStore";
import dayjs from "dayjs";

const roleColor: Record<string, string> = {
  super_admin: "red",
  support: "blue",
  billing: "green",
  developer: "purple",
};

export default function Admins() {
  const [showModal, setShowModal] = useState(false);
  const [messageApi, ctx] = message.useMessage();
  const [form] = Form.useForm();
  const { admin: currentAdmin } = useAuthStore();

  const { data: admins = [], isLoading } = useAdminList();

  const { mutate: createAdmin, isPending: creating } = useAdminCreate({
    onSuccess: () => {
      messageApi.success("Admin created.");
      setShowModal(false);
      form.resetFields();
    },
    onError: (err: any) => messageApi.error(err?.message || "Failed to create admin."),
  });

  const { mutate: toggleActive } = useAdminToggleActive({
    onSuccess: () => messageApi.success("Admin status updated."),
    onError: () => messageApi.error("Failed."),
  });

  const handleToggle = (admin: TPlatformAdmin) => {
    if (admin.id === currentAdmin?.id) {
      messageApi.warning("Cannot deactivate yourself.");
      return;
    }
    toggleActive({ id: admin.id, is_active: !admin.is_active });
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (n: string, row: TPlatformAdmin) => (
        <div>
          <div className="font-medium text-slate-800">
            {n} {row.id === currentAdmin?.id && <Tag color="purple" className="text-[10px]">You</Tag>}
          </div>
          <div className="text-xs text-slate-500">{row.email}</div>
        </div>
      ),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (r: string) => <Tag color={roleColor[r] || "default"}>{r.replace("_", " ").toUpperCase()}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      render: (a: boolean) => <Tag color={a ? "green" : "default"}>{a ? "Active" : "Inactive"}</Tag>,
    },
    {
      title: "Last Login",
      dataIndex: "last_login_at",
      key: "last_login_at",
      render: (d: string | null) => (d ? dayjs(d).format("DD MMM YYYY HH:mm") : "Never"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, row: TPlatformAdmin) => (
        <Popconfirm
          title={`${row.is_active ? "Deactivate" : "Activate"} this admin?`}
          onConfirm={() => handleToggle(row)}
          disabled={row.id === currentAdmin?.id}
        >
          <Button
            size="small"
            icon={<Power size={12} />}
            danger={row.is_active}
            disabled={row.id === currentAdmin?.id}
          >
            {row.is_active ? "Deactivate" : "Activate"}
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      {ctx}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">Platform Admins</h2>
          <p className="text-slate-500 text-sm mt-0.5">{admins.length} admin accounts</p>
        </div>
        <Button
          type="primary"
          icon={<Plus size={14} />}
          onClick={() => setShowModal(true)}
          style={{ background: "#4c1d95", borderColor: "#4c1d95" }}
        >
          Add Admin
        </Button>
      </div>

      <Card className="rounded-2xl border border-slate-200 shadow-sm" styles={{ body: { padding: 0 } }}>
        <Table
          dataSource={admins}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={false}
        />
      </Card>

      <Modal
        open={showModal}
        onCancel={() => { setShowModal(false); form.resetFields(); }}
        footer={null}
        title="Add Platform Admin"
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(vals) => {
            const { confirm_password, ...rest } = vals;
            createAdmin(rest);
          }}
          className="mt-4"
        >
          <Form.Item
            name="name"
            label="Full Name"
            rules={[{ required: true, message: "Full name is required" }, { min: 2, message: "Min 2 characters" }]}
          >
            <Input className="rounded-lg" placeholder="Jane Smith" />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email Address"
            rules={[{ required: true, message: "Email is required" }, { type: "email", message: "Enter a valid email" }]}
          >
            <Input className="rounded-lg" placeholder="jane@arruk.co.uk" />
          </Form.Item>
          <Form.Item
            name="password"
            label="Password"
            rules={[{ required: true, message: "Password is required" }, { min: 8, message: "Minimum 8 characters" }]}
          >
            <Input.Password className="rounded-lg" placeholder="Min 8 characters" />
          </Form.Item>
          <Form.Item
            name="confirm_password"
            label="Confirm Password"
            dependencies={["password"]}
            rules={[
              { required: true, message: "Please confirm the password" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) return Promise.resolve();
                  return Promise.reject(new Error("Passwords do not match"));
                },
              }),
            ]}
          >
            <Input.Password className="rounded-lg" placeholder="Re-enter password" />
          </Form.Item>
          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: "Role is required" }]}
            initialValue="support"
          >
            <Select
              options={[
                { value: "super_admin", label: "Super Admin" },
                { value: "support", label: "Support" },
                { value: "billing", label: "Billing" },
                { value: "developer", label: "Developer" },
              ]}
            />
          </Form.Item>
          <div className="flex gap-2 justify-end pt-2 border-t border-slate-100">
            <Button onClick={() => { setShowModal(false); form.resetFields(); }}>Cancel</Button>
            <Button
              htmlType="submit"
              type="primary"
              loading={creating}
              style={{ background: "#4c1d95", borderColor: "#4c1d95" }}
            >
              Create Admin
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
